<script setup lang="ts">
import { useCoreStore } from '../../stores/coreStore';
import SemboLogo from '../SemboLogo.vue';

import { DEFAULT_FOOTER, FOOTER_CONTENT } from './TheFooter.model';
import type { ContentItem, MarketSpecificContent } from './TheFooter.model';

const coreStore = useCoreStore();
const domain = `www.${coreStore.currentDomain}`;

const footer = (): MarketSpecificContent<ContentItem[][]> => {
  const footerContent = FOOTER_CONTENT[domain];
  return footerContent ?? DEFAULT_FOOTER;
};

function openFooterLink(link: string | undefined): void {
  if (link) {
    coreStore.openInNewTab(link);
  }
}

const currentYear = new Date().getFullYear();
</script>

<template>
  <footer class="footer">
    <SemboLogo color="color-soft-black" class="sembo-logo" />
    <div
      v-for="(row, rowIndex) in footer().content"
      :key="rowIndex"
      class="row"
    >
      <div
        v-for="(item, itemIndex) in row"
        :key="itemIndex"
        :class="{
          'info-item': !item.href,
          'link-item': !!item.href,
        }"
        @click="openFooterLink(item.href)"
      >
        {{ item.text }}
        <div
          v-if="rowIndex === 0 && row.length - 1 !== itemIndex"
          class="dot"
        />
      </div>
    </div>
    <div class="stena-line">
      © {{ currentYear }} Stena Line Travel Group AB
    </div>
  </footer>
</template>

<style scoped lang="scss">
.footer {
  padding: var(--spacing-48) var(--spacing-24);
  background: var(--color-sembo-purple);

  display: flex;
  flex-flow: column;
  align-items: center;
  margin-top: auto;

  .sembo-logo {
    margin-bottom: var(--spacing-24);
  }

  .row {
    display: flex;
    margin-bottom: var(--spacing-24);
    align-items: center;

    .info-item {
      @include font-body-m-light;
      display: flex;
      align-items: center;

      .dot {
        margin: var(--spacing-16);
        background: var(--color-neutral-70);
        width: 3px;
        height: 3px;
        border-radius: 50%;
      }
    }

    .link-item {
      @include font-link-m-light;
      text-decoration: underline;
      margin: var(--spacing-8);
      color: var(--color-soft-black);
      text-align: center;
      cursor: pointer;
    }
  }

  .stena-line {
    color: var(--color-neutral-70);
    @include font-body-l-light;
  }
}

@include for-less-or-equal-than-1200-px {
  .footer {
    .row {
      flex-flow: column;

      .info-item {
        margin-bottom: var(--spacing-16);

        .dot {
          display: none;
        }
      }
    }
  }
}
</style>
